import React from "react";
import { Download } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import PackerLineItem from "./PackerLineItem";
import type { PackerOrder } from "@/hooks/usePackers";

interface PackerOrderCardProps {
  order: PackerOrder;
  selected: boolean;
  onToggleSelect: (ordernumber: string) => void;
  onPrintLabel: (ordernumber: string) => void;
  isPrintingLabel: boolean;
  canWrite?: boolean;
}

const PackerOrderCard = ({ order, selected, onToggleSelect, onPrintLabel, isPrintingLabel, canWrite = true }: PackerOrderCardProps) => {
  const isPickup = order.status === 'processing_awaiting_pickup' || order.status === 'processing_ready_for_pickup';

  return (
    <div
      className={`rounded-xl p-4 transition-colors ${
        order.is_future_shipping
          ? 'bg-white/5 border-2 border-red-500/50'
          : isPickup
            ? 'bg-orange-500/5 border-2 border-orange-500/40'
            : 'bg-white/5 border border-white/10'
      }`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        {canWrite && (
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleSelect(order.ordernumber)}
            className="border-white/30 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
          />
        )}
        <span className="text-white font-bold text-base">{order.ordernumber}</span>
        {order.customer?.city && (
          <span className="bg-white/10 text-white/70 px-2 py-0.5 rounded text-xs">
            {order.customer.city}
          </span>
        )}
        <span className="bg-white/10 text-white/60 px-2 py-0.5 rounded text-xs">
          {order.item_count} items
        </span>
        {isPickup && (
          <span className="bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded text-xs font-medium">
            📦 Afhalen
          </span>
        )}
        {order.is_future_shipping && (
          <span className="bg-red-500/20 text-red-300 px-2 py-0.5 rounded text-xs font-medium">
            📅 Verzenddatum: {order.shippingdate
              ? new Date(order.shippingdate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
              : 'afwijkend'}
          </span>
        )}
        {order.is_printed && (
          <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded text-xs">
            Afgedrukt
          </span>
        )}
        {canWrite && (
          <div className="ml-auto">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onPrintLabel(order.ordernumber)}
              disabled={isPrintingLabel}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 h-8"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              Download
            </Button>
          </div>
        )}
      </div>

      {/* Line items */}
      <div className="pl-8">
        {order.line_items.map((item, idx) => (
          <PackerLineItem key={item.lineitemindex} item={item} ordernumber={order.ordernumber} lineitemindex={item.lineitemindex} />
        ))}
      </div>
    </div>
  );
};

export default PackerOrderCard;
